import { minBy, orderBy } from 'lodash';
import { FILTER } from '@fiverr-private/listing_lib';
import type { ActiveSideFiltersType, FilterType } from '@fiverr-private/listing_types';
import { TOP_GROUPING } from '../../../../../config/filters';
import { FILTER_MENU_CONTENT_MAPPING, MENU_CONTENT_TYPES } from '../../menus/consts';
import { getFilterDisplay } from '../../utils';
import { appTranslate } from '../../../../../../../shared/utils/i18n';
import { ALL_SUBCATEGORY } from '../../../../../../../lib/utils/side_filters/constants';
import { OPTION_PILLS_COUNT } from '../FilterRow.constants';
import type { CategoryExposure, FilterRowItemData } from '../FilterRow.types';
import { buildPillDropdownProps } from './buildPillDropdownProps';
import { buildCategoriesNavigation, CATEGORIES_MENU_ID } from './categoryNavigation';
import { getPriorityFilterTitle } from './getPriorityFilterTitle';

type CategoriesExposure = Extract<CategoryExposure, { mode: 'categories' }>;

interface BuildCategoryControlsParams {
    categoryExposure: CategoryExposure;
    filters: FilterType[];
    activeFilters: ActiveSideFiltersType;
    currencySymbol: string;
}

interface CategoryControls {
    dropdowns: FilterRowItemData[];
    options: FilterRowItemData[];
    /** Filters these controls already show, so they must not repeat as selected chips. */
    shownFilterIds: string[];
}

const findPriorityFilter = (filters: FilterType[], exposedFilterId?: string) =>
    filters.find(({ id, options }) => id === exposedFilterId && options?.length > 0) ??
    minBy(
        filters.filter(({ topGrouping }) => topGrouping === TOP_GROUPING.SERVICE),
        ({ order }) => order ?? Infinity
    );

const buildPriorityDropdown = (
    priorityFilter: FilterType,
    activeFilters: ActiveSideFiltersType,
    currencySymbol: string
): FilterRowItemData => ({
    type: 'dropdown',
    rowKey: `dropdown-${priorityFilter.id}`,
    props: {
        ...buildPillDropdownProps(currencySymbol, activeFilters),
        id: priorityFilter.id,
        data: priorityFilter.options,
        groupName: priorityFilter.topGrouping,
        ...getFilterDisplay(priorityFilter, activeFilters),
        title: getPriorityFilterTitle(priorityFilter),
        contentType: FILTER_MENU_CONTENT_MAPPING[priorityFilter.display_type],
    },
});

const buildPriorityOptions = (priorityFilter: FilterType, activeFilters: ActiveSideFiltersType): FilterRowItemData[] =>
    orderBy(priorityFilter.options, ({ count }) => count ?? 0, 'desc')
        .slice(0, OPTION_PILLS_COUNT)
        .map((option) => ({
            type: 'priorityOption',
            rowKey: `priorityOption-${priorityFilter.id}-${option.id}`,
            props: {
                filterId: priorityFilter.id,
                id: option.id,
                alias: option.alias,
                selected: option.selected,
                groupName: priorityFilter.topGrouping,
                activeFilters,
            },
        }));

const buildCategoriesDropdown = (
    { options }: CategoriesExposure,
    activeFilters: ActiveSideFiltersType,
    currencySymbol: string
): FilterRowItemData => {
    const selectedCategory = options.find(({ selected, id }) => selected && id !== ALL_SUBCATEGORY);
    const pillDropdownProps = buildPillDropdownProps(currencySymbol, activeFilters);

    return {
        type: 'dropdown',
        rowKey: `dropdown-${CATEGORIES_MENU_ID}`,
        props: {
            ...pillDropdownProps,
            additionalProps: { ...pillDropdownProps.additionalProps, inlineCount: true },
            ...buildCategoriesNavigation(options, activeFilters),
            id: CATEGORIES_MENU_ID,
            data: options,
            groupName: TOP_GROUPING.CATEGORY,
            title: selectedCategory?.alias ?? appTranslate('search_perseus.side_filters.filter_row_categories_title'),
            active: !!selectedCategory,
            contentType: MENU_CONTENT_TYPES.RADIO_LIST,
        },
    };
};

const buildCategoryOptions = (
    { topOptions }: CategoriesExposure,
    activeFilters: ActiveSideFiltersType
): FilterRowItemData[] =>
    topOptions.map((option) => ({
        type: 'categoryOption',
        rowKey: `categoryOption-${option.id}`,
        props: { option, activeFilters },
    }));

/**
 * The dropdown and the pills that follow it. They are either the Categories dropdown with the top NSCs, or the
 * category specific dropdown with its top values (the filter exposed for the query, else the first service filter).
 */
export const buildCategoryControls = ({
    categoryExposure,
    filters,
    activeFilters,
    currencySymbol,
}: BuildCategoryControlsParams): CategoryControls => {
    if (categoryExposure.mode === 'categories') {
        return {
            dropdowns: [buildCategoriesDropdown(categoryExposure, activeFilters, currencySymbol)],
            options: buildCategoryOptions(categoryExposure, activeFilters),
            shownFilterIds: [FILTER.SUB_CATEGORIES.ID],
        };
    }

    const priorityFilter = findPriorityFilter(filters, categoryExposure.filterId);

    return priorityFilter
        ? {
              dropdowns: [buildPriorityDropdown(priorityFilter, activeFilters, currencySymbol)],
              options: buildPriorityOptions(priorityFilter, activeFilters),
              shownFilterIds: [priorityFilter.id],
          }
        : { dropdowns: [], options: [], shownFilterIds: [] };
};
